import { placeCategory } from '../domain/mapping';
import { priceGlyph, type ScoredPlace } from '../domain/rank';
import { formatDistance, type Units } from '../domain/units';
import { el, spinner } from './dom';

const TOP_N = 3;

function mapsUrl(place: ScoredPlace): string {
  return (
    place.googleMapsUri ??
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${place.name} ${place.address}`,
    )}&query_place_id=${encodeURIComponent(place.id)}`
  );
}

function placeCard(place: ScoredPlace, top: boolean, units: Units): HTMLElement {
  const price = priceGlyph(place.priceLevel);
  const category = placeCategory(place);
  const rating =
    place.rating !== undefined
      ? `★ ${place.rating.toFixed(1)}${
          place.userRatingCount ? ` (${place.userRatingCount.toLocaleString()})` : ''
        }`
      : null;

  const openPill =
    place.openNow === undefined
      ? null
      : el(
          'span',
          { class: `pill ${place.openNow ? 'pill--open' : 'pill--closed'}` },
          place.openNow ? 'Open now' : 'Closed',
        );

  return el(
    'a',
    {
      class: `card${top ? ' card--top' : ''}`,
      href: mapsUrl(place),
      target: '_blank',
      rel: 'noopener noreferrer',
    },
    el('p', { class: 'card__name' }, place.name),
    // Category leads the row: it answers "what is this place" before any of the numbers
    // that only make sense once you know.
    el(
      'p',
      { class: 'card__meta' },
      category && el('span', { class: 'category' }, category),
      price && el('span', { class: 'price' }, price),
      rating && el('span', {}, rating),
      el('span', {}, formatDistance(place.distanceM, units)),
      openPill,
    ),
    el('p', { class: 'card__address' }, place.address),
  );
}

export interface ResultsViewProps {
  status: 'loading' | 'ready' | 'error';
  places: ScoredPlace[];
  shown: number;
  error: string | null;
  cached: boolean;
  units: Units;
  /** Set when the first search found nothing and a broader one was run instead. */
  widened: { from: string; to: string } | null;
  /**
   * How far "Show more" is allowed to go, in places. Deployment-wide and set from the
   * admin dashboard, which is why it arrives as a prop rather than living here as a
   * constant; `DEFAULT_MAX_RESULTS` is what applies until the server says otherwise.
   */
  maxShown: number;
  onShowMore: () => void;
  onStartOver: () => void;
  onRetry: () => void;
}

export function resultsView(props: ResultsViewProps): HTMLElement {
  if (props.status === 'loading') {
    return el(
      'div',
      { class: 'grow' },
      spinner(),
      el('p', { class: 'muted centered' }, 'Finding somewhere…'),
    );
  }

  if (props.status === 'error') {
    return el(
      'div',
      { class: 'grow' },
      el('p', { class: 'error' }, props.error ?? 'Something went wrong.'),
      el('button', { class: 'btn btn--primary', type: 'button', onclick: props.onRetry }, 'Try again'),
      el('button', { class: 'btn btn--ghost', type: 'button', onclick: props.onStartOver }, 'Start over'),
    );
  }

  if (props.places.length === 0) {
    // Carried over from the 2016 version, which spelled this across its three cards.
    return el(
      'div',
      { class: 'grow' },
      el('div', { class: 'empty' }, el('b', {}, 'Results'), el('b', {}, 'Not'), el('b', {}, 'Found')),
      el(
        'p',
        { class: 'muted centered' },
        'Nothing matched all of that. Try a wider search.',
      ),
      el('button', { class: 'btn btn--primary', type: 'button', onclick: props.onStartOver }, 'Start over'),
    );
  }

  // Never below one page: a ceiling under `TOP_N` would hide results the first screen
  // has already been built to show.
  const cap = Math.max(TOP_N, props.maxShown);
  const visible = props.places.slice(0, Math.min(props.shown, cap));
  const remaining = props.places.length - visible.length;
  const more = Math.min(remaining, TOP_N, cap - visible.length);

  return el(
    'div',
    { class: 'stack' },
    el('h2', { class: 'question__prompt' }, visible.length === 1 ? 'Here you go' : 'Top picks'),
    ...visible.map((place, index) => placeCard(place, index < TOP_N, props.units)),
    more > 0 &&
      el(
        'button',
        { class: 'btn', type: 'button', onclick: props.onShowMore },
        `Show ${more} more`,
      ),
    // Saying so matters: these are answers to a slightly different question than the
    // one the user answered, and silently swapping them would hide that.
    props.widened &&
      el(
        'p',
        { class: 'muted centered' },
        `Nothing nearby for ${props.widened.from}, so this is ${props.widened.to}.`,
      ),
    props.cached &&
      el('p', { class: 'muted centered' }, 'Showing a recent result.'),
    // Once the list is done growing, starting over is the only move left, so it stops
    // being a quiet footnote and takes the accent.
    el(
      'button',
      { class: `btn ${more > 0 ? 'btn--ghost' : 'btn--primary'}`, type: 'button', onclick: props.onStartOver },
      'Start over',
    ),
  );
}

export { TOP_N };
